import { type KeyboardEvent, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import * as Popover from '@radix-ui/react-popover'
import { ArrowLeft, CalendarDays, Check, Cpu, FileText, Search, Star, ThumbsDown, ThumbsUp, X } from 'lucide-react'
import { z } from 'zod'

import { type DatePreset, dayOfSince, dayOfUntil, matchingPreset, presetStart, readTimestamp } from './feedbackWindow'
import { promptText, readPromptVersion } from './versions'

import styles from './FeedbackFilterBar.module.scss'

import type { SummaryFeedbackRating } from '@/api/generated/api'
import type { SummaryFeedbackVersion } from '@/api/meetingSummaryFeedback'
import Button from '@/components/Button'
import { DAY_MS } from '@/utils/dates'

/** `since` and `until` are ISO timestamps; `until` is exclusive. */
export type FeedbackFilters = {
  promptVersion?: string
  summaryModel?: string
  rating?: SummaryFeedbackRating
  since?: string
  until?: string
}

type Field = 'prompt' | 'model' | 'rating'
type Choice = { field: Field; value?: string; label: string; detail?: string }

const FIELDS: Field[] = ['prompt', 'model', 'rating']
const FIELD_LABELS: Record<Field, string> = { prompt: 'Prompt', model: 'Model', rating: 'Rating' }
const FIELD_KEYS = { prompt: 'promptVersion', model: 'summaryModel', rating: 'rating' } as const
const RATING_LABELS: Record<SummaryFeedbackRating, string> = { up: '👍 Up', down: '👎 Down' }

const FacetIcon = ({ field, value, size = 16 }: { field: Field; value?: string; size?: number }) => {
  const Icon =
    field === 'prompt' ? FileText : field === 'model' ? Cpu : value === 'up' ? ThumbsUp : value ? ThumbsDown : Star
  return <Icon size={size} aria-hidden="true" className={styles.facetIcon} />
}

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/
const dayField = z.union([z.literal(''), z.string().regex(DAY_RE, 'Choose a date')])
const dateSchema = z
  .object({ from: dayField, to: dayField })
  .refine(({ from, to }) => !from || !to || from <= to, { message: 'End before start', path: ['to'] })
type DateValues = z.infer<typeof dateSchema>

const PRESETS: { value: DatePreset | undefined; label: string; trigger: string }[] = [
  { value: undefined, label: 'Any time', trigger: 'Any time' },
  { value: '1d', label: 'Today', trigger: 'Today' },
  { value: '7d', label: '7 days', trigger: 'Last 7 days' },
  { value: '30d', label: '30 days', trigger: 'Last 30 days' },
]

// Date inputs pick whole UTC days: `since` starts the first day and `until` ends the last (exclusive).
const sinceFromDay = (day: string) => (day ? readTimestamp(`${day}T00:00:00Z`) : undefined)
const untilFromDay = (day: string) => {
  const start = sinceFromDay(day)
  return start && new Date(Date.parse(start) + DAY_MS).toISOString()
}

const optionsFor = (field: Field, versions: SummaryFeedbackVersion[], values: FeedbackFilters) => {
  if (field === 'rating') return (['up', 'down'] as const).map((value) => ({ value, label: RATING_LABELS[value] }))
  const options = new Map<string, string>()
  for (const row of versions) {
    if (field === 'prompt' && row.promptVersion && !options.has(row.promptVersion))
      options.set(row.promptVersion, promptText(row.promptVersion, row.promptLabel))
    if (field === 'model' && row.model) options.set(row.model, row.model)
  }
  const applied = values[FIELD_KEYS[field]]
  if (applied && !options.has(applied)) options.set(applied, applied)
  return Array.from(options, ([value, label]) => ({ value, label }))
}

export const facetLabel = (field: Field, value: string, versions: SummaryFeedbackVersion[]) =>
  optionsFor(field, versions, {}).find((option) => option.value === value)?.label ?? value

const DateRange = ({ values, onChange }: { values: FeedbackFilters; onChange: (next: FeedbackFilters) => void }) => {
  const [isOpen, setIsOpen] = useState(false)
  const { since, until } = values
  const id = useId()
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<DateValues>({
    resolver: zodResolver(dateSchema),
    defaultValues: { from: dayOfSince(since), to: dayOfUntil(until) },
  })
  const activePreset = matchingPreset(since, until)
  const isAnyTime = !since && !until
  const trigger =
    PRESETS.find(({ value }) => (value ? value === activePreset : isAnyTime))?.trigger ??
    `${dayOfSince(since) || 'Start'} – ${dayOfUntil(until) || 'today'}`
  const apply = (next: Pick<FeedbackFilters, 'since' | 'until'>) => {
    onChange({ ...values, ...next })
    setIsOpen(false)
  }
  return (
    <Popover.Root
      open={isOpen}
      onOpenChange={(next) => {
        if (next) reset({ from: dayOfSince(since), to: dayOfUntil(until) })
        setIsOpen(next)
      }}
    >
      <Popover.Trigger asChild>
        <button
          type="button"
          className={styles.dateTrigger}
          aria-label={`Date range (UTC): ${trigger}`}
          title={`Date range (UTC): ${trigger}`}
        >
          <CalendarDays size={15} aria-hidden="true" />
          {trigger}
        </button>
      </Popover.Trigger>
      <Popover.Portal>
        <Popover.Content className={styles.datePopover} align="end" sideOffset={8} aria-label="Choose date range">
          <div className={styles.dateHeader}>
            <strong>Date range (UTC)</strong>
            <Popover.Close className={styles.iconButton} aria-label="Close date range">
              <X size={16} />
            </Popover.Close>
          </div>
          <div className={styles.presets} role="group" aria-label="Filter by date">
            {PRESETS.map(({ value, label }) => (
              <button
                type="button"
                key={label}
                aria-pressed={value ? activePreset === value : isAnyTime}
                onClick={() => apply({ since: value && presetStart(value), until: undefined })}
              >
                {label}
              </button>
            ))}
          </div>
          <form
            className={styles.dateForm}
            aria-label="Custom dates (UTC)"
            onSubmit={handleSubmit(({ from, to }) => apply({ since: sinceFromDay(from), until: untilFromDay(to) }))}
          >
            {(['from', 'to'] as const).map((name) => (
              <label key={name} htmlFor={`${id}-${name}`}>
                {name === 'from' ? 'From' : 'To'}
                <input
                  id={`${id}-${name}`}
                  type="date"
                  {...register(name)}
                  aria-invalid={Boolean(errors[name])}
                  aria-describedby={errors[name] ? `${id}-${name}-error` : undefined}
                />
                {errors[name] && (
                  <span id={`${id}-${name}-error`} role="alert" className={styles.error}>
                    {errors[name]?.message}
                  </span>
                )}
              </label>
            ))}
            <p>Whole UTC days, both dates included. Leave one empty for an open range.</p>
            <Button title="Apply dates" type="dark" htmlType="submit" onClick={() => undefined} />
          </form>
        </Popover.Content>
      </Popover.Portal>
    </Popover.Root>
  )
}

const FeedbackFilterBar = ({
  values,
  versions,
  onChange,
}: {
  values: FeedbackFilters
  versions: SummaryFeedbackVersion[]
  onChange: (next: FeedbackFilters) => void
}) => {
  const [isOpen, setIsOpen] = useState(false)
  const [query, setQuery] = useState('')
  const [field, setField] = useState<Field | null>(null)
  const [active, setActive] = useState(0)
  const input = useRef<HTMLInputElement>(null)
  const wrapper = useRef<HTMLDivElement>(null)
  const listId = useId()
  const closePicker = useCallback(() => {
    setIsOpen(false)
    setField(null)
    setQuery('')
    setActive(0)
  }, [])
  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (!wrapper.current?.contains(event.target as Node)) closePicker()
    }
    document.addEventListener('mousedown', outside)
    return () => document.removeEventListener('mousedown', outside)
  }, [closePicker])

  const choices = useMemo(() => {
    const text = query.trim().toLowerCase()
    const typedHash = readPromptVersion(text)
    const valueChoices = (key: Field, prefix: boolean) =>
      optionsFor(key, versions, values)
        .filter(({ value, label }) =>
          `${prefix ? FIELD_LABELS[key] : ''} ${label} ${value}`.toLowerCase().includes(text)
        )
        .map(({ value, label }) => ({ field: key, value, label: prefix ? `${FIELD_LABELS[key]}: ${label}` : label }))
    let options: Choice[]
    if (field) options = valueChoices(field, false)
    else {
      options = FIELDS.filter((key) => FIELD_LABELS[key].toLowerCase().includes(text)).map((key) => ({
        field: key,
        label: FIELD_LABELS[key],
        detail: 'Choose a value',
      }))
      if (text) options.push(...FIELDS.flatMap((key) => valueChoices(key, true)))
    }
    if (
      typedHash &&
      (field === null || field === 'prompt') &&
      !options.some((option) => option.field === 'prompt' && option.value === typedHash)
    )
      options.push({ field: 'prompt', value: typedHash, label: `Use “${typedHash}”`, detail: 'Exact prompt hash' })
    return options.slice(0, 60)
  }, [field, query, values, versions])
  const activeIndex = Math.min(active, choices.length - 1)

  const choose = (choice: Choice) => {
    setQuery('')
    setActive(0)
    if (choice.value === undefined) {
      setField(choice.field)
      return
    }
    onChange({ ...values, [FIELD_KEYS[choice.field]]: choice.value })
    closePicker()
  }
  const keyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault()
      setIsOpen(true)
      const delta = event.key === 'ArrowDown' ? 1 : -1
      setActive(isOpen && choices.length ? (Math.max(activeIndex, 0) + delta + choices.length) % choices.length : 0)
    } else if (event.key === 'Enter' && isOpen && choices[activeIndex]) {
      event.preventDefault()
      choose(choices[activeIndex])
    } else if (event.key === 'Escape') {
      event.preventDefault()
      closePicker()
    }
  }
  const activeFields = FIELDS.filter((key) => values[FIELD_KEYS[key]])

  return (
    <section className={styles.container} aria-label="Feedback filters">
      <div className={styles.bar}>
        <div
          className={styles.searchArea}
          ref={wrapper}
          onBlur={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node | null)) closePicker()
          }}
        >
          <div className={styles.searchRow}>
            <Search size={17} aria-hidden="true" />
            {field && (
              <button
                type="button"
                className={styles.fieldBack}
                onClick={() => {
                  setField(null)
                  setQuery('')
                  setActive(0)
                  input.current?.focus()
                }}
                aria-label="Back to all filters"
              >
                <ArrowLeft size={14} />
                {FIELD_LABELS[field]}
              </button>
            )}
            <input
              ref={input}
              type="text"
              role="combobox"
              aria-label="Search feedback filters"
              aria-autocomplete="list"
              aria-expanded={isOpen}
              aria-controls={isOpen ? listId : undefined}
              aria-activedescendant={isOpen && activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
              value={query}
              placeholder={
                field ? `Search ${FIELD_LABELS[field].toLowerCase()}…` : 'Filter by prompt, model or rating…'
              }
              onFocus={() => setIsOpen(true)}
              onClick={() => setIsOpen(true)}
              onKeyDown={keyDown}
              onChange={(event) => {
                setQuery(event.target.value)
                setActive(0)
                setIsOpen(true)
              }}
            />
          </div>
          {isOpen && (
            <div className={styles.picker}>
              <div className={styles.pickerHeading}>{field ? FIELD_LABELS[field] : 'Choose a filter'}</div>
              <div
                id={listId}
                role="listbox"
                aria-label={field ? `${FIELD_LABELS[field]} values` : 'Feedback filter options'}
                className={styles.options}
              >
                {choices.map((choice, index) => (
                  <div
                    role="option"
                    aria-label={choice.label}
                    aria-description={choice.detail}
                    id={`${listId}-${index}`}
                    key={`${choice.field}:${choice.value ?? ''}`}
                    aria-selected={index === activeIndex}
                    className={styles.option}
                    data-active={index === activeIndex}
                    onMouseDown={(event) => event.preventDefault()}
                    onMouseMove={() => setActive(index)}
                    onClick={() => choose(choice)}
                  >
                    <span className={styles.optionLabel}>
                      <FacetIcon field={choice.field} value={choice.value} />
                      <span>
                        {choice.label}
                        {choice.detail && <small>{choice.detail}</small>}
                      </span>
                    </span>
                    {choice.value !== undefined && values[FIELD_KEYS[choice.field]] === choice.value && (
                      <Check size={15} aria-label="Applied" />
                    )}
                  </div>
                ))}
              </div>
              {choices.length === 0 && (
                <p className={styles.help} role="status">
                  {field === 'prompt' ? 'No matching prompts. Type a 12-character hash.' : 'No matching filters.'}
                </p>
              )}
            </div>
          )}
        </div>
        <DateRange values={values} onChange={onChange} />
      </div>
      {activeFields.length > 0 && (
        <div className={styles.tags} aria-label="Applied filters">
          {activeFields.map((key) => {
            const value = values[FIELD_KEYS[key]] as string
            return (
              <span
                className={styles.tag}
                key={key}
                role="group"
                aria-label={`${FIELD_LABELS[key]} is ${facetLabel(key, value, versions)}`}
              >
                <FacetIcon field={key} value={value} size={14} />
                {FIELD_LABELS[key]}
                <select
                  className={styles.chipValue}
                  aria-label={`Change ${FIELD_LABELS[key]} filter`}
                  value={value}
                  onChange={(event) => onChange({ ...values, [FIELD_KEYS[key]]: event.target.value })}
                >
                  {optionsFor(key, versions, values).map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <button
                  type="button"
                  className={styles.iconButton}
                  aria-label={`Clear ${FIELD_LABELS[key]} filter`}
                  onClick={() => {
                    input.current?.focus()
                    setIsOpen(false)
                    onChange({ ...values, [FIELD_KEYS[key]]: undefined })
                  }}
                >
                  <X size={13} />
                </button>
              </span>
            )
          })}
          <button
            type="button"
            className={styles.reset}
            onClick={() =>
              onChange({ ...values, promptVersion: undefined, summaryModel: undefined, rating: undefined })
            }
          >
            Reset filters
          </button>
        </div>
      )}
    </section>
  )
}

export default FeedbackFilterBar
